import React from 'react';

import type { BoundingBox } from '../../types';

export interface HighlightRect {
  id: string;
  boundingBox: BoundingBox;
  isActive?: boolean;
  color?: string;
}

interface DocumentHighlightOverlayProps {
  highlights: HighlightRect[];
  containerWidth: number;
  containerHeight: number;
  onHighlightClick?: (id: string) => void;
}

const DEFAULT_COLOR = 'rgba(59, 130, 246, 0.25)';
const ACTIVE_COLOR = 'rgba(59, 130, 246, 0.45)';

/**
 * Renders highlight rectangles over a document preview.
 * Bounding boxes are normalized (0-1), scaled to container dimensions.
 */
const DocumentHighlightOverlay: React.FC<DocumentHighlightOverlayProps> = ({
  highlights,
  containerWidth,
  containerHeight,
  onHighlightClick,
}) => {
  if (containerWidth === 0 || containerHeight === 0) return null;

  return (
    <div className='absolute inset-0 pointer-events-none'>
      {highlights.map(({ id, boundingBox, isActive, color }) => (
        <div
          key={id}
          className={`absolute cursor-pointer pointer-events-auto transition-colors duration-150 ${
            isActive ? 'ring-2 ring-blue-500 z-10' : 'hover:ring-1 hover:ring-blue-400'
          }`}
          style={{
            left: boundingBox.left * containerWidth,
            top: boundingBox.top * containerHeight,
            width: boundingBox.width * containerWidth,
            height: boundingBox.height * containerHeight,
            backgroundColor: isActive
              ? (color ?? ACTIVE_COLOR)
              : (color ?? DEFAULT_COLOR),
            borderRadius: 2,
          }}
          onClick={() => onHighlightClick?.(id)}
          role='button'
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              onHighlightClick?.(id);
            }
          }}
          aria-label={`Highlight ${id}`}
        />
      ))}
    </div>
  );
};

export default DocumentHighlightOverlay;
